import { Checkbox, FormControlLabel } from '@mui/material';
import { hasEvacContent } from 'features/evacZones/utils/hasEvacContent';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormEvacZoneStatus } from 'shared/types';
import { removeHTMLTags } from 'shared/utils';

type SharedDeactivationFormValues = {
  manualDeactivationStarted: boolean | null;
  evacuationOrders: string;
  evacuationWarnings: string;
  evacuationAdvisories: string;
  evacuationShelterInPlace: string;
  evacuationNotes: string;
  evacZoneStatuses: FormEvacZoneStatus[];
  customOrders: boolean;
  customWarnings: boolean;
  customAdvisories: boolean;
  customShelterInPlace: boolean;
};

type BaseManualDeactivationFieldProps = {
  hasEvacZones: boolean;
  isExternallyBlocked?: boolean;
  isExternallyDisabled?: boolean;
};

/**
 * Manual DeactivationField component
 * Handles deactivation of reporter-managed incidents based on backend rules.
 *
 * The checkbox is disabled and automatically unchecked when:
 * - External conditions prevent deactivation
 * - Any evacuation data exists (orders, warnings, advisories, notes, or active zones)
 *
 * The checkbox is disabled when:
 * - External conditions require disabling or external blockers exist
 */
export const BaseManualDeactivationField = (
  props: BaseManualDeactivationFieldProps,
): JSX.Element => {
  const {
    hasEvacZones,
    isExternallyBlocked = false,
    isExternallyDisabled = false,
  } = props;
  const { t } = useTranslation();

  const { control, setValue, formState, watch } =
    useFormContext<SharedDeactivationFormValues>();

  const [
    manualDeactivationStarted,
    evacuationOrders,
    evacuationWarnings,
    evacuationAdvisories,
    evacuationShelterInPlace,
    evacuationNotes,
    evacZoneStatuses,
    customOrders,
    customWarnings,
    customAdvisories,
    customShelterInPlace,
  ] = watch([
    'manualDeactivationStarted',
    'evacuationOrders',
    'evacuationWarnings',
    'evacuationAdvisories',
    'evacuationShelterInPlace',
    'evacuationNotes',
    'evacZoneStatuses',
    'customOrders',
    'customWarnings',
    'customAdvisories',
    'customShelterInPlace',
  ]);

  const hasEvacuationOrders = hasEvacContent({
    evacContent: evacuationOrders,
    customEvac: customOrders,
    hasEvacZones,
  });
  const hasEvacuationWarnings = hasEvacContent({
    evacContent: evacuationWarnings,
    customEvac: customWarnings,
    hasEvacZones,
  });
  const hasEvacuationAdvisories = hasEvacContent({
    evacContent: evacuationAdvisories,
    customEvac: customAdvisories,
    hasEvacZones,
  });
  const hasEvacuationShelterInPlace = hasEvacContent({
    evacContent: evacuationShelterInPlace,
    customEvac: customShelterInPlace,
    hasEvacZones,
  });
  const hasEvacuationNotes = Boolean(removeHTMLTags(evacuationNotes ?? ''));

  const isDeactivationBlocked = Boolean(
    isExternallyBlocked ||
      evacZoneStatuses.length ||
      hasEvacuationOrders ||
      hasEvacuationWarnings ||
      hasEvacuationAdvisories ||
      hasEvacuationShelterInPlace ||
      hasEvacuationNotes,
  );

  useEffect(() => {
    if (isDeactivationBlocked && manualDeactivationStarted) {
      setValue('manualDeactivationStarted', false);
    }
  }, [isDeactivationBlocked, manualDeactivationStarted, setValue]);

  const disabled =
    formState.isSubmitting || isExternallyDisabled || isDeactivationBlocked;

  return (
    <Controller
      name="manualDeactivationStarted"
      control={control}
      render={({ field }): JSX.Element => (
        <FormControlLabel
          control={
            <Checkbox
              {...field}
              value="manualDeactivationStarted"
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              id="field-control-manualDeactivationStarted"
            />
          }
          label={t('editGeoEvent.startDeactivation')}
          disabled={disabled}
          sx={{ width: 'fit-content' }}
        />
      )}
    />
  );
};
